import { ColumnDef } from '@tanstack/react-table'
import { EditPencil } from 'iconoir-react'
import { DateComponent } from 'app/components/DateComponent'
import { integrationEndpointSlug } from 'app/components/integrations-locations/integrationStatus'
import { LazyWorkspaceCell } from 'app/components/LazyWorkspaceCell'
import { LazyIntegrationStatus } from 'app/components/shared/styled/LazyIntegrationStatus'
import { ActionsCell } from 'app/components/table/ActionsCell'
import { FilterValueType, FilterableColumn } from 'app/filter.utils'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { IntegrationsRow, WorkspaceIntegrationsRow } from 'app/types/integrations'
import { Jsonify } from 'lib/toJSON'

export const IntegrationIcon = ({
  integration,
}: {
  integration: IntegrationsRow[`integration`] | `Facebook Ads` | `Facebook Leads`
}) => {
  switch (integration) {
    case `CallRail`:
      return <img className="mr-2 h-8 w-auto" src="/public/callrail.png" alt="CallRail" />
    case `Jotform`:
      // The PNG bakes in an opaque white background; mat it so it does not
      // render as a stray white box on a dark row.
      return (
        // ast-grep-ignore: no-raw-neutral-color
        <span className="mr-2 inline-flex shrink-0 rounded bg-white p-0.5">
          <img className="h-7 w-auto" src="/public/jotform.png" alt="Jotform" />
        </span>
      )
    case `Sikka`:
      return (
        // ast-grep-ignore: no-raw-neutral-color
        <span className="mr-2 inline-flex shrink-0 rounded bg-white p-0.5">
          <img className="h-7 w-auto" src="/public/sikka_square_logo.png" alt="Sikka" />
        </span>
      )
    case `Facebook Ads`:
      return <img className="mr-2 h-8 w-auto" src="/public/meta_icon.png" alt="Facebook Ads" />
    case `Facebook Leads`:
      return <img className="mr-2 h-8 w-auto" src="/public/meta_icon.png" alt="Facebook Leads" />
    case `Google`:
      return <img className="mr-2 h-8 w-auto" src="/public/google_ads.png" alt="Google Ads" />
    case `Carestack`:
      return <img className="mr-2 h-8 w-auto" src="/public/carestack.png" alt="Carestack" />
    case `Ascend`:
      return <img className="mr-2 h-8 w-auto" src="/public/ascend.png" alt="Ascend" />
    case `RingCentral`:
      return <img className="mr-2 h-8 w-auto" src="/public/ringcentral.png" alt="RingCentral" />
    case `Kolla`:
      return (
        // ast-grep-ignore: no-raw-neutral-color
        <span className="mr-2 inline-flex shrink-0 rounded bg-white p-0.5">
          <img className="h-7 w-auto" src="/public/kolla.png" alt="Kolla" />
        </span>
      )
    case `Gravity Forms`:
      return (
        // ast-grep-ignore: no-raw-neutral-color
        <span className="mr-2 inline-flex shrink-0 rounded bg-white p-0.5">
          <img className="h-7 w-auto" src="/public/gravity.png" alt="Gravity Forms" />
        </span>
      )
    case `Webflow Forms`:
      return (
        // ast-grep-ignore: no-raw-neutral-color
        <span className="mr-2 inline-flex shrink-0 rounded bg-white p-0.5">
          <img className="h-5 w-auto" src="/public/webflow.png" alt="Webflow Forms" />
        </span>
      )
    case `Twilio`:
      return <img className="mr-2 h-8 w-auto" src="/public/twilio.png" alt="Twilio" />
    case `Dynamic Number Insertion`:
      return <img className="mr-2 h-8 w-auto" src="/public/trackable.png" alt="Dynamic Number Insertion" />
    default:
      return null
  }
}

export const defaultColumns = (
  onIntegrationClick: (integrationsRow: IntegrationsRow) => void,
): ColumnDef<IntegrationsRow, any>[] => [
  {
    accessorKey: `integration`,
    header: `Integration`,
    id: `Integration`,
    meta: {
      defaultVisible: true,
      getCellStyle: () => `h-12`,
      ControlledComponent: (row: IntegrationsRow | undefined) =>
        row == null ? (
          <></>
        ) : (
          <ActionsCell
            content={
              <div className="flex items-center gap-x-1">
                <IntegrationIcon integration={row?.integration ?? ``} />
                <span className="text-ui-xs">{row?.integration ?? ``}</span>
              </div>
            }
            tooltipContent={row?.integration ?? ``}
            onClick={() => onIntegrationClick(row ?? {})}
            actions={
              <div className="flex h-6 w-6 items-center justify-center rounded border border-strong bg-surface-subtle p-1.5">
                <EditPencil className="text-strong" />
              </div>
            }
          />
        ),
    },
  },
  { accessorKey: `locationId`, header: `Location ID`, id: `Location ID`, meta: { defaultVisible: false } },
  { accessorKey: `locationName`, header: `Location`, id: `Location`, meta: { defaultVisible: true } },
  {
    accessorKey: `workspaceId`,
    header: `Workspace`,
    id: `Workspace`,
    cell: (props) => <LazyWorkspaceCell workspaceId={props.getValue()} />,
    meta: { defaultVisible: true },
  },
  {
    accessorKey: `ids`,
    header: `Integration ID`,
    id: `Integration ID`,
    meta: { defaultVisible: true },
  },
  {
    accessorKey: `status`,
    header: `Status`,
    id: `Status`,
    cell: (props) => (
      <LazyIntegrationStatus
        locationId={props.row.original.locationId}
        integration={integrationEndpointSlug(props.row.original.integration)}
      />
    ),
    meta: { defaultVisible: true },
  },
  {
    accessorKey: `lastUpdate`,
    header: `Last Update`,
    id: `Last Update`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.getValue()} />,
    meta: { defaultVisible: true },
  },
]

export const filterableColumns = (
  locationOptions: { value: string; name: string; id: string }[],
): FilterableColumn[] => [
  {
    attribute: `locationId`,
    displayName: `Location`,
    type: FilterValueType.multiple,
    options: locationOptions,
  },
  {
    attribute: `workspaceId`,
    displayName: `Workspaces`,
    type: FilterValueType.id,
    fetcher: (params) => gqlFetchList('workspaces', { search: params.query }, 'id name'),
  },
  {
    attribute: `status`,
    displayName: `Status`,
    type: FilterValueType.multiple,
    options: [
      { id: `Active`, value: `Active`, name: `Active` },
      { id: `In Progress`, value: `In Progress`, name: `In Progress` },
      { id: `Broken`, value: `Broken`, name: `Broken` },
    ],
  },
  {
    attribute: `integration`,
    displayName: `Integration`,
    type: FilterValueType.multiple,
    options: [
      { id: `Sikka`, value: `Sikka`, name: `Sikka` },
      { id: `CallRail`, value: `CallRail`, name: `CallRail` },
      { id: `Jotform`, value: `Jotform`, name: `Jotform` },
      { id: `Facebook`, value: `Facebook`, name: `Facebook` },
      { id: `Google`, value: `Google`, name: `Google` },
      { id: `Carestack`, value: `Carestack`, name: `Carestack` },
      { id: `RingCentral`, value: `RingCentral`, name: `RingCentral` },
      { id: `Kolla`, value: `Kolla`, name: `Kolla` },
      { id: `ElevenLabs`, value: `ElevenLabs`, name: `ElevenLabs` },
    ],
  },
]

export const defaultWorkspaceColumns = (
  onIntegrationClick: (integrationsRow: Jsonify<WorkspaceIntegrationsRow>) => void,
): ColumnDef<Jsonify<WorkspaceIntegrationsRow>, any>[] => [
  {
    accessorKey: `integration`,
    header: `Integration`,
    id: `Integration`,
    cell: (props) => (
      <div className="flex w-full cursor-pointer items-center" onClick={() => onIntegrationClick(props.row.original)}>
        <IntegrationIcon integration={props.getValue()} /> <span className="text-ui-xs">{props.getValue()}</span>
      </div>
    ),
    meta: { defaultVisible: true },
  },
  // { accessorKey: `workspaceId`, header: `Workspace ID`, id: `Workspace ID`, meta: { defaultVisible: false } },
  // { accessorKey: `workspaceName`, header: `Workspace`, id: `Workspace`, meta: { defaultVisible: true } },
  {
    accessorKey: `ids`,
    header: `Integration ID`,
    id: `Integration ID`,
    meta: { defaultVisible: true },
  },
  {
    accessorKey: `lastUpdate`,
    header: `Last Update`,
    id: `Last Update`,
    minSize: 120,
    cell: (props) => <DateComponent value={props.getValue()} dateOnly useLocalUtc />,
    meta: { defaultVisible: true },
  },
]

export const filterableWorkspaceColumns = (): FilterableColumn[] => [
  {
    attribute: `integration`,
    displayName: `Integration`,
    type: FilterValueType.multiple,
    options: [
      // { id: `Sikka`, value: `Sikka`, name: `Sikka` },
      // { id: `CallRail`, value: `CallRail`, name: `CallRail` },
      // { id: `Jotform`, value: `Jotform`, name: `Jotform` },
      // { id: `Facebook`, value: `Facebook`, name: `Facebook` },
      // { id: `Google`, value: `Google`, name: `Google` },
      { id: `Carestack`, value: `Carestack`, name: `Carestack` },
      { id: `RingCentral`, value: `RingCentral`, name: `RingCentral` },
    ],
  },
]
